/**
 * projectContainerSectionsIntakeEventToState
 *
 * Applies realtime intake events (added/removed/moved/updated) to the
 * ProjectContainerSectionModel[] home-feed state for project containers.
 *
 * This keeps the per-section folders + intakes list in sync with
 * ProjectContainerEvent updates from SignalR without requiring refetches.
 */
import type {
  ProjectContainerSectionModel,
  ProjectContainerOverviewFolder,
  ProjectContainerOverviewIntake
} from 'src/features/companyProjects/projectContainers/types/ProjectContainerHomeTypes';
import { normalizeHomeFeedIntake } from 'src/features/companyProjects/projectContainers/dashboard/api/normalizeHomeFeedSections';
import type { ProjectContainerEvent } from '../events/projectContainerEventTypes';
import { ProjectSectionIntakeEventType } from '../events/projectContainerEventTypes';

// ----- Sections/home-feed helpers -----

const cloneSections = (
  state: ProjectContainerSectionModel[] | undefined
): ProjectContainerSectionModel[] | undefined => {
  if (!state) return state;
  return state.map((section) => ({
    ...section,
    Folders: section.Folders
      ? section.Folders.map<ProjectContainerOverviewFolder>((folder) => ({
          ...folder,
          Intakes: folder.Intakes ? [...folder.Intakes] : undefined
        }))
      : section.Folders
  }));
};

// ----- Sections/home-feed reducer (intake events) -----

export const applyContainerEventToSectionsState = (
  state: ProjectContainerSectionModel[] | undefined,
  event: ProjectContainerEvent
): ProjectContainerSectionModel[] | undefined => {
  const current = state;
  if (!current || current.length === 0) {
    return state;
  }

  const { intakeId, fromFolderId, toFolderId, data, type } = event;

  switch (type) {
    case ProjectSectionIntakeEventType.IntakeAdded: {
      if (!toFolderId || !data) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== toFolderId) return folder;
          const existing = folder.Intakes ?? [];
          const withoutDuplicate = existing.filter((x) => x.Id !== intakeId);
          const newItem = normalizeHomeFeedIntake({ ...(data as object as Record<string, unknown>), Id: intakeId });
          sectionChanged = true;
          return {
            ...folder,
            Intakes: [newItem, ...withoutDuplicate]
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeRemoved: {
      if (!fromFolderId) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== fromFolderId || !folder.Intakes) return folder;
          const filtered = folder.Intakes.filter((x) => x.Id !== intakeId);
          if (filtered.length === folder.Intakes.length) {
            return folder;
          }
          sectionChanged = true;
          return {
            ...folder,
            Intakes: filtered
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeMoved: {
      // toFolderId is always required. fromFolderId is absent when assigning from
      // the global inbox into a container — backend sends FromFolderId null; domain
      // still runs MarkAsUnread after MoveIntake so `data` carries HasNewMessage.
      if (!toFolderId) return state;

      // Find the existing intake in the original state so we can
      // preserve its Subject (and any other fields we track locally)
      // when moving between folders. Some move events do not include
      // a full `data` payload, which previously caused the Subject to
      // be dropped in other windows.
      let movedIntake: ProjectContainerOverviewIntake | undefined = undefined;

      if (fromFolderId != null) {
        for (const section of current) {
          if (!section.Folders) continue;
          for (const folder of section.Folders) {
            if (folder.Id !== fromFolderId || !folder.Intakes) continue;
            const found = folder.Intakes.find((x) => x.Id === intakeId);
            if (found) {
              movedIntake = found;
              break;
            }
          }
          if (movedIntake) break;
        }
      }

      const next = cloneSections(current);
      if (!next) return state;

      const buildNewItem = (): ProjectContainerOverviewIntake => {
        const merged =
          movedIntake != null
            ? { ...movedIntake, ...(data as object as Record<string, unknown>) }
            : { ...(data as object as Record<string, unknown>), Id: intakeId };
        return normalizeHomeFeedIntake(merged);
      };

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (fromFolderId != null && folder.Id === fromFolderId && folder.Intakes) {
            const filtered = folder.Intakes.filter((x) => x.Id !== intakeId);
            if (filtered.length !== folder.Intakes.length) {
              sectionChanged = true;
              return {
                ...folder,
                Intakes: filtered
              };
            }
            return folder;
          }

          if (folder.Id === toFolderId) {
            const existing = folder.Intakes ?? [];
            const withoutDuplicate = existing.filter((x) => x.Id !== intakeId);

            const newItem = buildNewItem();

            sectionChanged = true;
            return {
              ...folder,
              Intakes: [newItem, ...withoutDuplicate]
            };
          }

          return folder;
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeUpdated: {
      if (!toFolderId || !data) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== toFolderId || !folder.Intakes) return folder;
          const intakes = folder.Intakes.map((item) => {
            if (item.Id !== intakeId) return item;
            sectionChanged = true;
            return normalizeHomeFeedIntake({ ...item, ...(data as object as Record<string, unknown>) });
          });
          if (sectionChanged) {
            return {
              ...folder,
              Intakes: intakes
            };
          }
          return folder;
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    default:
      return state;
  }
};
